import * as React from 'react';
import styled, { css } from 'styled-components';

import { composeRefs } from '../../internals/composeRefs';
import type { FloatingAlign, FloatingSide } from '../../internals/computeFloatingPosition';
import { createUIContext } from '../../internals/createContext';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Presence } from '../../internals/Presence';
import { Portal } from '../../internals/Portal';
import { Slot } from '../../internals/Slot';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useFloatingPosition } from '../../internals/useFloatingPosition';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

export type TooltipSize = 'sm' | 'md' | 'lg';
export type TooltipVariant = 'dark' | 'light';

export interface TooltipRootProps {
  children: React.ReactNode;
  side?: FloatingSide;
  align?: FloatingAlign;
  /** Delay before opening (ms). Matches legacy `useHover` delayEnter. */
  delayDuration?: number;
  /** Delay before closing after leaving trigger (ms). Matches legacy `delayLeave`. */
  closeDelay?: number;
  variant?: TooltipVariant;
  size?: TooltipSize;
  showArrow?: boolean;
}

interface TooltipContextValue {
  open: boolean;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  side: FloatingSide;
  align: FloatingAlign;
  variant: TooltipVariant;
  size: TooltipSize;
  showArrow: boolean;
  tooltipId: string;
  scheduleShow: () => void;
  scheduleHide: () => void;
  onContentPointerEnter: () => void;
  onContentPointerLeave: () => void;
}

const [TooltipProvider, useTooltipContext] = createUIContext<TooltipContextValue>('Tooltip');

function useTooltipTimers(delayDuration: number, closeDelay: number) {
  const [open, setOpen] = React.useState(false);
  const showTimer = React.useRef<ReturnType<typeof window.setTimeout>>();
  const hideTimer = React.useRef<ReturnType<typeof window.setTimeout>>();
  const contentHoveredRef = React.useRef(false);

  const clearShowTimer = React.useCallback(() => {
    if (showTimer.current != null) {
      window.clearTimeout(showTimer.current);
      showTimer.current = undefined;
    }
  }, []);

  const clearHideTimer = React.useCallback(() => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = undefined;
    }
  }, []);

  const scheduleShow = React.useCallback(() => {
    clearHideTimer();
    clearShowTimer();
    showTimer.current = window.setTimeout(() => {
      setOpen(true);
    }, delayDuration);
  }, [delayDuration, clearHideTimer, clearShowTimer]);

  const scheduleHide = React.useCallback(() => {
    clearShowTimer();
    clearHideTimer();
    hideTimer.current = window.setTimeout(() => {
      if (!contentHoveredRef.current) {
        setOpen(false);
      }
    }, closeDelay);
  }, [closeDelay, clearShowTimer, clearHideTimer]);

  const onContentPointerEnter = React.useCallback(() => {
    contentHoveredRef.current = true;
    clearHideTimer();
    clearShowTimer();
    setOpen(true);
  }, [clearHideTimer, clearShowTimer]);

  const onContentPointerLeave = React.useCallback(() => {
    contentHoveredRef.current = false;
    scheduleHide();
  }, [scheduleHide]);

  const forceClose = React.useCallback(() => {
    contentHoveredRef.current = false;
    clearShowTimer();
    clearHideTimer();
    setOpen(false);
  }, [clearShowTimer, clearHideTimer]);

  React.useEffect(() => {
    return () => {
      clearShowTimer();
      clearHideTimer();
    };
  }, [clearShowTimer, clearHideTimer]);

  return {
    open,
    scheduleShow,
    scheduleHide,
    onContentPointerEnter,
    onContentPointerLeave,
    forceClose,
  };
}

const TooltipRoot = ({
  children,
  side = 'top',
  align = 'center',
  delayDuration = 100,
  closeDelay = 100,
  variant = 'dark',
  size = 'md',
  showArrow = true,
}: TooltipRootProps) => {
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const tooltipId = React.useId();
  const { open, scheduleShow, scheduleHide, onContentPointerEnter, onContentPointerLeave, forceClose } =
    useTooltipTimers(delayDuration, closeDelay);

  useEscapeKey(forceClose, open);

  const value = React.useMemo(
    () => ({
      open,
      triggerRef,
      contentRef,
      side,
      align,
      variant,
      size,
      showArrow,
      tooltipId,
      scheduleShow,
      scheduleHide,
      onContentPointerEnter,
      onContentPointerLeave,
    }),
    [
      open,
      side,
      align,
      variant,
      size,
      showArrow,
      tooltipId,
      scheduleShow,
      scheduleHide,
      onContentPointerEnter,
      onContentPointerLeave,
    ],
  );

  return <TooltipProvider value={value}>{children}</TooltipProvider>;
};

TooltipRoot.displayName = 'Tooltip.Root';

export interface TooltipTriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  semanticId?: string;
}

const TooltipTrigger = React.forwardRef<HTMLElement, TooltipTriggerProps>(function TooltipTrigger(
  { asChild = false, semanticId, onMouseEnter, onMouseLeave, onFocus, onBlur, ...rest },
  ref,
) {
  const { scheduleShow, scheduleHide, triggerRef, open, tooltipId } = useTooltipContext();
  const semanticProps = getSemanticAttributes(semanticId);
  const composedRef = React.useMemo(() => composeRefs(ref, triggerRef), [ref, triggerRef]);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    onMouseEnter?.(event);
    if (!event.defaultPrevented) {
      scheduleShow();
    }
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    onMouseLeave?.(event);
    if (!event.defaultPrevented) {
      scheduleHide();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
    onFocus?.(event);
    if (!event.defaultPrevented) {
      scheduleShow();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
    onBlur?.(event);
    if (!event.defaultPrevented) {
      scheduleHide();
    }
  };

  const a11y = {
    'aria-describedby': open ? tooltipId : undefined,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  if (asChild) {
    return (
      <Slot
        ref={composedRef}
        {...a11y}
        {...semanticProps}
        {...(rest as React.ComponentProps<typeof Slot>)}
      />
    );
  }

  return (
    <span
      ref={composedRef as React.Ref<HTMLSpanElement>}
      tabIndex={rest.tabIndex ?? 0}
      {...a11y}
      {...semanticProps}
      {...rest}
      style={{
        display: 'inline-block',
        cursor: 'inherit',
        ...rest.style,
      }}
    />
  );
});

TooltipTrigger.displayName = 'Tooltip.Trigger';

const TooltipBody = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$size', '$variant'].includes(String(prop)),
})<{
  $size: TooltipSize;
  $variant: TooltipVariant;
}>`
  position: relative;
  border-radius: var(--ui-radius-sm, ${rem(4)});
  max-width: ${rem(500)};
  word-break: break-word;
  overflow-wrap: break-word;
  filter: drop-shadow(${rem(0)} ${rem(2)} ${rem(4)} var(--ui-shadow-color));
  white-space: normal;
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  font-weight: 400;

  ${({ $variant }) => {
    if ($variant === 'dark') {
      return css`
        background: var(--ui-text);
        color: var(--ui-text-inverse);

        span {
          color: var(--ui-text-inverse);
        }
      `;
    }
    return css`
      background: var(--ui-surface);
      color: var(--ui-text);
    `;
  }};

  ${({ $size }) => {
    if ($size === 'sm') {
      return css`
        padding: ${rem(10)} ${rem(11)};
      `;
    }
    if ($size === 'lg') {
      return css`
        padding: ${rem(14)};
      `;
    }
    return css`
      padding: ${rem(12)} ${rem(8)};
    `;
  }};
`;

const TooltipArrow = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$variant', '$side'].includes(String(prop)),
})<{ $variant: TooltipVariant; $side: FloatingSide }>`
  position: absolute;
  width: 0;
  height: 0;

  ${({ $variant, $side }) => {
    const color = $variant === 'dark' ? 'var(--ui-text)' : 'var(--ui-surface)';

    switch ($side) {
      case 'bottom':
        return css`
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid ${color};
        `;
      case 'left':
        return css`
          right: -6px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-left: 6px solid ${color};
        `;
      case 'right':
        return css`
          left: -6px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-right: 6px solid ${color};
        `;
      default:
        return css`
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid ${color};
        `;
    }
  }};
`;

const StyledTooltipLayer = styled.div`
  position: fixed;
  z-index: ${zIndex.tooltip};
  top: 0;
  left: 0;
  pointer-events: auto;
`;

export interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(function TooltipContent(
  { semanticId, style, children, ...rest },
  ref,
) {
  const { open, triggerRef, contentRef, side, align, variant, size, showArrow, tooltipId, onContentPointerEnter, onContentPointerLeave } =
    useTooltipContext();
  const semanticProps = getSemanticAttributes(semanticId);

  const { coords } = useFloatingPosition({
    open,
    side,
    align,
    offsetPx: 8,
    triggerRef,
    floatingRef: contentRef,
  });

  const composedRef = React.useMemo(() => composeRefs(ref, contentRef), [ref, contentRef]);

  return (
    <Portal>
      <Presence present={open}>
        <StyledTooltipLayer
          ref={composedRef}
          id={tooltipId}
          role="tooltip"
          data-state={open ? 'open' : 'closed'}
          data-side={side}
          onMouseEnter={onContentPointerEnter}
          onMouseLeave={onContentPointerLeave}
          style={{
            top: coords.top,
            left: coords.left,
            ...style,
          }}
          {...semanticProps}
          {...rest}
        >
          <TooltipBody $size={size} $variant={variant}>
            {children}
            {showArrow ? <TooltipArrow $variant={variant} $side={side} /> : null}
          </TooltipBody>
        </StyledTooltipLayer>
      </Presence>
    </Portal>
  );
});

TooltipContent.displayName = 'Tooltip.Content';

export const Tooltip = {
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Content: TooltipContent,
};
